import Button from "../../UI/Button/Button";
import Count from "../../UI/Count/Count";
import Cover from "../../UI/Cover/Cover";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrow_down.svg";
import "./ServiceBox.css";
import { useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { stateType } from "../../store";
import Rating from "../Rating/Rating";
import { setReview } from "../../state_management/actions/common";
import { reviewType } from "../../state_management/reducers/common_reload";
import { getAllServices } from "../../state_management/epics";
import { category, service } from "../../state_management/actionTypes";
import { addToCart, changeCartItem } from "../../state_management/actions";
import {
  Item,
  cartInitialState,
  cartItem
} from "../../state_management/reducers/cart";
import { useInCart } from "../../utilities/cart";
import { useAsync } from "../../utilities/asyncFunc";
import { currency } from "../../utilities/misc";
import { view_list_item_ga } from "../../utilities/analytics";
import { json_key, locale, translate_options } from "../../translate";
import { cart_item_from_service } from "../../utilities/cartUtilities";
import { LocaleContext } from "../../AppProvider";
import SeeBeforeAfterButton from "./ServiceBoxGallery";

type changeCartQuantityType = typeof changeCartItem;
const ServiceBox = ({
  listHidden,
  categoryObject,
  Name,
  defaultServices,
  categoryIDs
}: {
  listHidden: boolean;
  categoryObject?: category;
  defaultServices?: service[];
  Name: string;
  categoryIDs: number[];
}) => {
  const [services, setServices] = useState<service[]>(defaultServices || []);

  useEffect(() => {
    if (services.length && !res.loading) {
      view_list_item_ga({
        item_list_id: categoryObject?.id,
        item_list_name: Name,
        items: services,
        extra: false
      });
    }
  }, [services]);

  useEffect(() => {
    if (defaultServices?.length) {
      setServices([...defaultServices]);
    }
  }, [defaultServices]);

  const res = useAsync(
    {
      asyncFunc: getAllServices,
      funcParams: {
        categories: categoryIDs
      },
      immediate: true,
      canCall: !defaultServices
    },
    [JSON.stringify(categoryIDs)]
  );

  useAsync(
    {
      asyncFunc: getAllServices,
      funcParams: {
        categories: categoryIDs,
        background: true
      },
      immediate: true
    },
    [JSON.stringify(categoryIDs)]
  );

  useEffect(() => {
    if (res.data) {
      setServices([...res.data]);
    }
  }, [res.data]);

  return (
    <div className={`service_box_wrapper ${res.loading ? "overlay" : ""}`}>
      <Rating />
      {services
        .sort((a, b) => {
          if (a.position === null && b.position !== null) {
            return 1;
          } else if (a.position !== null && b.position === null) {
            return -1;
          } else {
            // For non-null values, perform regular sorting
            return a.position - b.position;
          }
        })
        .map((service, n) => (
          <ServiceBoxInner key={n} service={service} listHidden={listHidden} />
        ))}
    </div>
  );
};

const mapStateToProps = (state: stateType) => ({
  cart: state.cartReducer.cart,
  cartItems: state.cartReducer
});
const mapDispatchToProps = (dispatch: any) => ({
  setReview: (data: reviewType) => {
    dispatch(setReview(data));
  },
  addToCart: (data: Item) => {
    dispatch(addToCart(data));
  },
  changeCartItem: (data: Parameters<changeCartQuantityType>[0]) => {
    dispatch(changeCartItem(data));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ServiceBox);

const ServiceBoxInner = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  ({
    cartItems,
    service,
    addToCart,
    listHidden
  }: {
    cartItems: cartInitialState;
    service: service;
    labelled?: boolean;
    addToCart: (data: Item) => void;
    setReview: (data: reviewType) => void;
    listHidden: boolean;
  }) => {
    const { cart } = cartItems;
    const { t, locale } = useContext(LocaleContext);
    const mountedRef = useRef(true);
    const [openDescription, setOpenDescription] = useState(false);
    const [isAdded, setIsAdded] = useState(false);
    const [timeoutId, setTimeoutId] = useState<number | undefined>();
    const cart_item = useInCart(service.id, cart);
    const [count, setCount] = useState(cart_item?.quantity || 1);
    const [giftValue, setGiftValue] = useState<
      | undefined
      | {
          service_id: number;
          value: number;
        }
    >(
      cart_item?.type === "gift"
        ? { service_id: cart_item.id, value: cart_item.item.price }
        : undefined
    );

    const cartAdd = (service: service) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (cart_item) {
        return;
      } else {
        const Item = cart_item_from_service({ service, giftValue, count });
        if (!Item) {
          return;
        }

        addToCart(Item);

        setIsAdded(true);
        const copyTimeout = window.setTimeout(() => {
          if (mountedRef.current) {
            setIsAdded(false);
          }
        }, 3000);
        setTimeoutId(copyTimeout);
      }
    };
    useEffect(() => {
      return () => {
        mountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      if (!cart_item) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        setIsAdded(false);
      }
    }, [cart_item]);

    const isCustomUrl = service.type === "custom_url";

    const buttonText =
      service.type === "custom_url"
        ? service.button_label
        : service.type === "gift"
        ? t("Purchase")
        : isAdded
        ? t("ADDED")
        : t("ADD TO CART");

    return (
      <div className={`service_box box ${listHidden ? "listHidden" : ""}`}>
        <div className="service_box_top flex">
          <div className="service_img_wrapper">
            <Cover
              labelled={
                service.discount
                  ? service.price - service.price_with_discount
                  : undefined
              }
              className=""
            >
              <img src={service.photo_path || ""} alt="" />
            </Cover>
          </div>
          <div className="service_top_body flex flex1">
            <div className="flex1 noShowMd service_top_body_innerLg">
              <div className="service_body_pane flex flex1">
                <div className="flex1 service_price_rating_wrapper">
                  <div className="title service_price_rating_title">
                    {service.name}
                  </div>
                  {service.type === "gift" || isCustomUrl ? (
                    <></>
                  ) : (
                    <div className="amount">
                      <span className="med_text">
                        {currency(
                          service.price_with_discount.toFixed(2),
                          locale
                        )}
                      </span>
                      {service.discount ? (
                        <span className="strike_through">
                          {currency(service.price.toFixed(2), locale)}
                        </span>
                      ) : (
                        ``
                      )}
                    </div>
                  )}
                  {/* Hide ratings */}
                  {/* <div className="rating">
                    <div className="rating_info_cont">
                      <Stars
                        rating={service.reviews_count ? service.avg_rate : 0}
                      />
                      <span
                        onClick={() => {
                          // Only set the review if there are 1 or more reviews
                          if (service.reviews_count) {
                            setReview({
                              service: service.name,
                              serviceId: service.id
                            });
                          }
                        }}
                        className="rating_count"
                      >
                        {service.reviews_count} review
                        {service.reviews_count === 1 ? `` : `s`}
                      </span>
                    </div>
                  </div> */}
                </div>

                <div className="service_body_btn_wrapper center">
                  <Count
                    invisible={service.type === "gift" || isCustomUrl}
                    extCount={count}
                    extSetCount={setCount}
                    service={service}
                  />
                  <Button
                    size={"sm"}
                    linkObj={
                      service.type === "custom_url"
                        ? {
                            href: service.url,
                            external: true,
                            target: "_blank"
                          }
                        : service.type === "gift"
                        ? {
                            href: "https://giftcard.sumup.io/order/G9Y6YUE/oven-clean-eireann-cleanbee-",
                            external: true,
                            target: "_blank"
                          }
                        : undefined
                    }
                    plane={isAdded}
                    span
                    // disabled={service.type === "gift" && !giftValue}
                    onClick={(e) => {
                      if (isCustomUrl) {
                        return;
                      }
                      e.stopPropagation();
                      cartAdd(service);
                    }}
                  >
                    {buttonText}
                  </Button>
                </div>
              </div>
              <div className="servicebox_body_wrapper">
                <div className="body">
                  <span
                    className="flex1 servicebox_body_desc"
                    dangerouslySetInnerHTML={{ __html: service.description }}
                  />
                  <div className="service_box_hidden_body_button">
                    <Button size={"sm"}>
                      {service.type === "custom_url"
                        ? service.button_label
                        : service.type === "gift"
                        ? t("Purchase")
                        : t("ADD TO CART")}
                    </Button>
                  </div>
                </div>
                <ServiceInclusion
                  count={count}
                  setGiftValue={setGiftValue}
                  giftValue={giftValue}
                  tag={
                    service.type === "gift" ? "gift_selectable" : "inclusion"
                  }
                  service={service}
                  setOpenDescription={setOpenDescription}
                  openDescription={openDescription}
                  t={t}
                  locale={locale}
                  large={true}
                />
                {((service.before_after_images &&
                  !!service.before_after_images.length) ||
                  (service.videos && !!service.videos.length)) && (
                  <SeeBeforeAfterButton
                    caption={service.name}
                    images={service.before_after_images || []}
                    videos={service.videos || []}
                  />
                )}
              </div>
            </div>
            <div className="fdCol flex1 showMd service_top_body_innerSm">
              <div className="service_body_pane fdCol flex1">
                <div className="title med_text">{service.name}</div>
                {service.type === "gift" || isCustomUrl ? (
                  <></>
                ) : (
                  <div className="amount">
                    <span className="med_text">
                      {currency(service.price_with_discount.toFixed(2), locale)}
                    </span>
                    {service.discount ? (
                      <span className="strike_through">
                        {currency(service.price.toFixed(2), locale)}
                      </span>
                    ) : (
                      ``
                    )}
                  </div>
                )}
                {/* Hide ratings */}
                {/* <div className="rating">
                  <div className="rating_info_cont">
                    <Stars
                      rating={service.reviews_count ? service.avg_rate : 0}
                    />
                    <span
                      onClick={() => {
                        // Only set the review if there are 1 or more reviews
                        if (service.reviews_count) {
                          setReview({
                            service: service.name,
                            serviceId: service.id
                          });
                        }
                      }}
                      className="rating_count"
                    >
                      {service.reviews_count} review
                      {service.reviews_count === 1 ? `` : `s`}
                    </span>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
        <div className="service_box_bottom">
          <div className="service_body_pane flex1">
            <div
              className="body"
              dangerouslySetInnerHTML={{ __html: service.description }}
            />
            <ServiceInclusion
              count={count}
              setGiftValue={setGiftValue}
              giftValue={giftValue}
              tag={service.type === "gift" ? "gift_selectable" : "inclusion"}
              service={service}
              setOpenDescription={setOpenDescription}
              openDescription={openDescription}
              t={t}
              locale={locale}
              large={false}
            />
            {((service.before_after_images &&
              !!service.before_after_images.length) ||
              (service.videos && !!service.videos.length)) && (
              <SeeBeforeAfterButton
                caption={service.name}
                images={service.before_after_images || []}
                videos={service.videos || []}
              />
            )}

            <div className="service_body_btn_wrapper span_full center">
              <Count
                invisible={service.type === "gift" || isCustomUrl}
                extCount={count}
                extSetCount={setCount}
                service={service}
              />
              <Button
                size={"sm"}
                span
                linkObj={
                  service.type === "custom_url"
                    ? {
                        href: service.url,
                        external: true,
                        target: "_blank"
                      }
                    : service.type === "gift"
                    ? {
                        href: "https://giftcard.sumup.io/order/G9Y6YUE/oven-clean-eireann-cleanbee-",
                        external: true,
                        target: "_blank"
                      }
                    : undefined
                }
                // disabled={service.type === "gift" && !giftValue}
                plane={isAdded}
                onClick={(e) => {
                  if (service.type === "custom_url") {
                    return;
                  }
                  e.stopPropagation();
                  cartAdd(service);
                }}
              >
                {buttonText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const ServiceInclusion = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  ({
    tag,
    service,
    setOpenDescription,
    openDescription,
    t,
    large,
    setGiftValue,
    cart
  }: {
    count: number;
    tag: "inclusion" | "gift_selectable";
    service: service;
    setOpenDescription: (dat: boolean) => void;
    openDescription: boolean;
    t: (originalText: json_key, options?: translate_options) => string;
    locale: locale;
    large: boolean;
    setGiftValue: (
      data: { service_id: number; value: number } | undefined
    ) => void;
    giftValue:
      | {
          service_id: number;
          value: number;
        }
      | undefined;
    changeCartItem: (data: Parameters<changeCartQuantityType>[0]) => void;
    cart: cartItem[];
  }) => {
    const cart_item = useInCart(service.id, cart);
    useEffect(() => {
      if (!cart_item?.quantity) {
        setGiftValue(undefined);
      }
    }, [cart_item?.quantity]);

    return tag === "gift_selectable" ? (
      <div className="expand flex">
        <div className="flex1">
          {/*  <div className="inner plane">
            <span className="med_text">
              {t("Choose the gift card’s value")}
            </span>
          </div>
          <div className="expandText flex inc_select_wrapper">
          {service.gift_values.map((gift, n) => (
              <ButtonPlane
                key={n}
                onClick={(e) => {
                  e.stopPropagation();
                  change(gift);
                }}
                className={`${large ? "bigIn,cText" : ""} ${
                  giftValue?.value === gift.value &&
                  giftValue.service_id === gift.service_id
                    ? "selected_gift"
                    : ""
                }`}
                size={"sm"}
              >
                {currency(gift.value.toFixed(2), locale, true)}
              </ButtonPlane>
            ))} 
          </div>*/}
        </div>
        {large ? (
          <div className="service_box_hidden_body_button">
            <Button
              linkObj={{
                href: "https://giftcard.sumup.io/order/G9Y6YUE/oven-clean-eireann-cleanbee-",
                external: true,
                target: "_blank"
              }}
              size={"sm"}
            >
              {t("Purchase")}
            </Button>
          </div>
        ) : (
          <></>
        )}
      </div>
    ) : service.included_services.length ? (
      <div className="expand flex">
        <div className="flex1">
          <div
            className="inner"
            onClick={() => setOpenDescription(!openDescription)}
          >
            <span className="med_text">
              {service.included_label || t("What’s included?")}
            </span>
            <ArrowDownIcon />
          </div>
          {openDescription ? (
            <div className="expandText">
              <ul>
                {service.included_services.map((extra, n) => (
                  <li key={n}>{extra.name};</li>
                ))}
              </ul>
            </div>
          ) : (
            <></>
          )}
        </div>
        {large ? (
          <div className="service_box_hidden_body_button">
            <Button size={"sm"}>
              {service.type === "custom_url"
                ? service.button_label
                : t("ADD TO CART")}
            </Button>
          </div>
        ) : (
          <></>
        )}
      </div>
    ) : (
      <></>
    );
  }
);
